import { createContext, useState, useEffect, useCallback, useMemo, useContext, ReactNode, useRef } from 'react';
import { useNavigate, useLocation, Location } from 'react-router-dom';
import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios';

// Types
import { AuthUser } from '../types/auth';
import { authApi, LoginCredentials as ApiLoginCredentials } from '../api/authApi';

// Token utilities
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken,
} from '@/utils/token';

// Constants
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before token expires

// Types
type LoginMethod = 'email' | 'username';

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  updateUser: (user: AuthUser) => void;
  login: (identifier: string, password: string, method?: LoginMethod) => Promise<AuthUser | undefined>;
  logout: (redirectTo?: string) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create context with default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
};

// Clear all auth data from storage
const clearSession = (): void => {
  removeAuthToken();
  removeRefreshToken();
  localStorage.removeItem('user');
};

// Extend Window interface for testing
declare global {
  interface Window {
    __authContext?: AuthContextType;
    Cypress?: any;
    store?: {
      auth?: {
        isAuthenticated: boolean;
        user: AuthUser | null;
        token: string | null;
      };
      authContext?: AuthContextType;
    };
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // ...existing code...

  // Update user in state and localStorage
  const updateUser = useCallback((user: AuthUser) => {
    setState(prev => ({
      ...prev,
      user,
    }));
    localStorage.setItem('user', JSON.stringify(user));
  }, []);
  const [state, setState] = useState<AuthState>(initialState);
  const navigate = useNavigate();
  const location = useLocation() as Location<LocationState>;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshPromise, setRefreshPromise] = useState<Promise<boolean> | null>(null);

  // Clear auth data and update state
  const clearAuthData = useCallback((): void => {
    clearSession();
    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      error: null,
    }));
  }, []);

  // Logout function - declared early to avoid reference errors
  const logout = useCallback(async (redirectTo: string = '/login'): Promise<void> => {
    try {
      // Try to call the logout API if we have a token
      const token = getAuthToken();
      if (token) {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Error during logout API call:', error);
          // Continue with local logout even if API call fails
        }
      }
      
      // Clear all auth data and reset state
      clearAuthData();
      
      // Redirect to login page or specified path
      navigate(redirectTo, { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, we should still clear the auth state
      clearAuthData();
      navigate('/login', { replace: true });
    }
  }, [clearAuthData, navigate]);

  // Make logout available for other functions
  const logoutRef = useRef(logout);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Handle token refresh with proper error handling and race condition management
  const handleRefreshToken = useCallback(async (): Promise<boolean> => {
    // If already refreshing, return the existing promise
    if (isRefreshing) {
      return refreshPromise || Promise.resolve(false);
    }

    setIsRefreshing(true);
    const refreshToken = getRefreshToken();
    
    // If no refresh token, clear auth and return false
    if (!refreshToken) {
      console.warn('No refresh token available');
      clearAuthData();
      setIsRefreshing(false);
      return false;
    }

    // Create a new promise for the refresh operation
    const refreshPromiseInstance = (async (): Promise<boolean> => {
      try {
        console.log('Attempting to refresh token...');
        const response = await authApi.refreshToken(refreshToken) as RefreshTokenResponse;
        
        if (!response?.accessToken) {
          throw new Error('No access token in refresh response');
        }

        // Update tokens in storage
        setAuthToken(response.accessToken);
        if (response.refreshToken) {
          setRefreshToken(response.refreshToken);
        } else {
          // If no new refresh token provided, keep the existing one
          console.log('No new refresh token provided, keeping existing one');
        }

        // Update application state
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          token: response.accessToken,
          loading: false,
          error: null,
        }));

        console.log('Token refresh successful');
        return true;
      } catch (error) {
        console.error('Error refreshing token:', error);
        
        // Only clear auth data if it's an actual error (not a network error)
        if (error instanceof Error && !error.message.includes('Network Error')) {
          console.warn('Clearing auth data due to refresh token error');
          clearAuthData();
        }
        
        throw error; // Re-throw to be caught by the interceptor
      } finally {
        setIsRefreshing(false);
        setRefreshPromise(null);
      }
    })();

    setRefreshPromise(refreshPromiseInstance);
    return refreshPromiseInstance;
  }, [isRefreshing, refreshPromise, clearAuthData]);

  // Remove the old logout function that was here
  
  // Check if user has a specific permission by code
  const hasPermission = useCallback((permissionCode: string): boolean => {
    if (!state.user) return false;
    
    const role = state.user.role;
    if (!role) return false;
    
    // If role is a string, we can't check permissions
    if (typeof role === 'string') return false;
    
    // If user has admin role, they have all permissions
    if (role.code === 'SYSTEM_ADMIN' || role.name === 'SYSTEM_ADMIN') {
      return true;
    }
    
    // Check if permissions exist and user has the required permission
    if (!role.permissions || !Array.isArray(role.permissions)) {
      return false;
    }
    
    return role.permissions.some(permission => permission.code === permissionCode);
  }, [state.user]);

  // Check if user has any of the specified permission codes
  const hasAnyPermission = useCallback((permissionCodes: string[]): boolean => {
    if (!state.user) return false;
    
    const role = state.user.role;
    if (!role) return false;
    
    // If role is a string, we can't check permissions
    if (typeof role === 'string') return false;
    
    // If user has admin role, they have all permissions
    if (role.code === 'SYSTEM_ADMIN' || role.name === 'SYSTEM_ADMIN') {
      return true;
    }
    
    // Check if permissions exist
    if (!role.permissions || !Array.isArray(role.permissions)) {
      return false;
    }
    
    return role.permissions.some(permission => 
      permissionCodes.includes(permission.code)
    );
  }, [state.user]);

  // Check if user has all of the specified permission codes
  const hasAllPermissions = useCallback((permissionCodes: string[]): boolean => {
    if (!state.user || !permissionCodes.length) return false;
    
    const role = state.user.role;
    if (!role) return false;
    
    // If role is a string, we can't check permissions
    if (typeof role === 'string') return false;
    
    // If user has admin role, they have all permissions
    if (role.code === 'SYSTEM_ADMIN' || role.name === 'SYSTEM_ADMIN') {
      return true;
    }
    
    // Check if permissions exist
    if (!role.permissions || !Array.isArray(role.permissions)) {
      return false;
    }
    
    const userPermissionCodes = role.permissions.map(p => p.code);
    return permissionCodes.every(code => userPermissionCodes.includes(code));
  }, [state.user]);

  // Check if user has a specific role by code or name
  const hasRole = useCallback((roleCode: string): boolean => {
    if (!state.user) return false;
    
    const role = state.user.role;
    if (!role) return false;
    
    if (typeof role === 'string') {
      return role === roleCode;
    }
    
    return role.code === roleCode || role.name === roleCode;
  }, [state.user]);

  // Check if user has any of the specified role codes or names
  const hasAnyRole = useCallback((roleCodes: string[]): boolean => {
    if (!state.user) return false;
    
    const role = state.user.role;
    if (!role) return false;
    
    if (typeof role === 'string') {
      return roleCodes.includes(role);
    }
    
    return roleCodes.includes(role.code) || roleCodes.includes(role.name);
  }, [state.user]);

  // Login function
  const login = useCallback(async (
    identifier: string, 
    password: string, 
    method: LoginMethod = 'username'
  ): Promise<AuthUser | undefined> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const credentials: ApiLoginCredentials = method === 'email'
        ? { email: identifier, password }
        : { username: identifier, password };
      
      const response = await authApi.login(credentials);
      
      if (!response?.data) {
        throw new Error('Invalid response from server');
      }

      const { user, accessToken, refreshToken } = response.data;
      
      if (!accessToken) {
        throw new Error('No access token received in response');
      }
      
      if (!user || !user.id) {
        throw new Error('Invalid user data received from server');
      }
      
      if (!user.role) {
        throw new Error('Your account is not properly configured. Please contact support.');
      }
      
      // Set the access token
      setAuthToken(accessToken);
      
      // Only set refresh token if it's provided in the response
      if (refreshToken) {
        setRefreshToken(refreshToken);
      } else {
        // Ensure any existing refresh token is cleared if not provided
        removeRefreshToken();
      }
      localStorage.setItem('user', JSON.stringify(user));

      const newAuthState: AuthState = {
        isAuthenticated: true,
        user,
        token: accessToken,
        loading: false,
        error: null,
      };

      setState(newAuthState);

      // Expose auth context for testing
      if (typeof window !== 'undefined') {
        window.__authContext = {
          ...newAuthState,
          login,
          logout,
          refreshToken: handleRefreshToken,
          hasPermission,
          hasAnyPermission,
          hasAllPermissions,
          hasRole,
          hasAnyRole,
          updateUser,
        };
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });

      return user;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'An unknown error occurred during login';
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      
      throw new Error(errorMessage);
    }
  }, [navigate, location.state, handleRefreshToken, logout, hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole]);

  // Set up axios interceptors
  const setupAxiosInterceptors = useCallback(() => {
    // Request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Don't add auth header for login/refresh endpoints
        if (config.url?.includes('/auth/')) {
          return config;
        }

        const token = getAuthToken();
        if (token) {
          // Create a new headers object with the Authorization header
          const headers = {
            ...(config.headers as Record<string, unknown>),
            Authorization: `Bearer ${token}`
          };
          
          // Return a new config with the updated headers
          return {
            ...config,
            headers
          } as InternalAxiosRequestConfig;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for handling token refresh
    const responseInterceptor = axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean; _isRetry?: boolean }) | undefined;

        // Skip if no config or already retried
        if (!originalRequest || originalRequest._isRetry) {
          return Promise.reject(error);
        }

        // Only handle 401 Unauthorized errors
        if (error.response?.status === 401) {
          // Skip token refresh for login/refresh endpoints to avoid infinite loops
          if (originalRequest.url?.includes('/auth/')) {
            return Promise.reject(error);
          }

          // Mark request as retried to prevent infinite loops
          originalRequest._isRetry = true;
          
          try {
            console.log('Attempting to refresh token due to 401 response');
            const refreshed = await handleRefreshToken();
            
            if (refreshed) {
              const newToken = getAuthToken();
              if (newToken) {
                // Clone the original request with new token
                const newRequest = {
                  ...originalRequest,
                  headers: {
                    ...originalRequest.headers,
                    Authorization: `Bearer ${newToken}`
                  },
                  // Clear the _isRetry flag for the retry
                  _isRetry: false
                };
                
                console.log('Retrying original request with new token');
                return axios(newRequest);
              }
            }
            
            // If we get here, refresh failed or no new token
            console.warn('Token refresh failed or no new token received');
            await logoutRef.current();
            return Promise.reject(new Error('Session expired. Please log in again.'));
            
          } catch (refreshError) {
            console.error('Error during token refresh:', refreshError);
            await logoutRef.current();
            return Promise.reject(refreshError);
          }
        }

        // For non-401 errors, just reject with the original error
        return Promise.reject(error);
      }
    );

    // Cleanup function to remove interceptors
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [handleRefreshToken]);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      try {
        const token = getAuthToken();
        const userJson = localStorage.getItem('user');
        
        if (token && userJson) {
          const user = JSON.parse(userJson) as AuthUser;
          
          setState(prev => ({
            ...prev,
            isAuthenticated: true,
            user,
            token,
            loading: false,
          }));

          // Set up axios interceptors after successful initialization
          setupAxiosInterceptors();
        } else {
          setState(prev => ({
            ...prev,
            loading: false,
            isAuthenticated: false,
          }));
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        clearAuthData();
      }
    };

    void initializeAuth();
  }, [clearAuthData, setupAxiosInterceptors]);

  // Create the context value
  const contextValue = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      logout,
      refreshToken: handleRefreshToken,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      hasAnyRole,
      updateUser,
    }),
    [state, login, logout, handleRefreshToken, hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole, updateUser]
  );

// (rest of file remains unchanged)


  
  // Update the window context when values change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__authContext = contextValue;
    }
  }, [contextValue]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
